'use client';

/**
 * useWorkflowRun — live status of a workflow-engine run
 * (story-entities-redesign §3, the single progress pattern).
 *
 * Two entry modes:
 *   useWorkflowRun(runId)            — engine runId is already known.
 *   useWorkflowRun({ startKey })     — resumable UI: the portal stored only
 *     its producer startKey (audio_stories.batch_run_id / books.pages_run_id);
 *     the initial fetch resolves it via GET /api/workflows/by-start-key/[key]
 *     and SSE events are then filtered by the resolved runId.
 *
 * Initial fetch: GET /api/workflows/[runId] (owlcat_jobs via portal).
 * Live updates: 'workflow.run.updated' SSE events (WorkflowEventBridge) —
 * patched into the React Query cache, NO interval polling. Terminal events
 * (workflow.completed / workflow.failed) trigger one authoritative refetch.
 * The bridge is at-least-once → dedup by payload.workflowEventId.
 */

import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEventSourceEvent } from './useEventSourceEvent';
import { EVENT_TYPES } from '@/shared/infrastructure/events/eventTypes';
import { whileWaiting } from '@/shared/hooks/liveRefetch';

export interface WorkflowRunStep {
  stepId: string;
  total?: number;
  done?: number;
}

export interface WorkflowRunStatus {
  runId: string;
  workflow: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  currentStep: string | null;
  error: string | null;
  steps: WorkflowRunStep[];
  startedAt: string;
  updatedAt: string;
}

/** Either the engine runId, or the producer startKey to resolve it from. */
export type WorkflowRunRef = string | { startKey: string | null } | null;

const workflowRunQueryKey = (runId: string) => ['workflow-run', runId] as const;
const workflowRunStartKeyQueryKey = (startKey: string) =>
  ['workflow-run', 'start-key', startKey] as const;

function patchStep(
  steps: WorkflowRunStep[],
  stepId: string,
  patch: Partial<WorkflowRunStep>,
): WorkflowRunStep[] {
  const known = steps.some((s) => s.stepId === stepId);
  if (!known) return [...steps, { stepId, ...patch }];
  return steps.map((s) => (s.stepId === stepId ? { ...s, ...patch } : s));
}

export function useWorkflowRun(ref: WorkflowRunRef) {
  const queryClient = useQueryClient();
  const seenEventIdsRef = useRef<Set<number>>(new Set());

  const directRunId = typeof ref === 'string' ? ref : null;
  const startKey = ref !== null && typeof ref === 'object' ? ref.startKey : null;
  const enabled = !!directRunId || !!startKey;

  const queryKey = startKey
    ? workflowRunStartKeyQueryKey(startKey)
    : workflowRunQueryKey(directRunId ?? 'none');

  // New run reference → fresh dedup window.
  useEffect(() => {
    seenEventIdsRef.current = new Set();
  }, [directRunId, startKey]);

  const query = useQuery<WorkflowRunStatus | null>({
    queryKey,
    queryFn: async () => {
      const url = startKey
        ? `/api/workflows/by-start-key/${encodeURIComponent(startKey)}`
        : `/api/workflows/${directRunId}`;
      const res = await fetch(url);
      // startKey mode: the engine mints the child run slightly AFTER the
      // parent starts, so a 404 here is a RACE, not an error — return null
      // and let the poll below retry until it resolves. Without this the
      // query errors permanently, runId never resolves, and the SSE handler
      // (guarded on runId) never patches → a dead progress panel.
      if (res.status === 404 && startKey) return null;
      if (!res.ok) {
        throw new Error(`Failed to load workflow run ${startKey ?? directRunId}: ${res.status}`);
      }
      return res.json();
    },
    enabled,
    // Poll only until the run resolves (startKey race) or while it is still
    // running as a safety net; SSE drives live updates once resolved.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return startKey ? 2000 : false; // unresolved startKey → keep trying
      const s = data.status;
      // resolved: workflow.run.updated drives it (below); the interval is only the fallback
      return whileWaiting(!(s === 'completed' || s === 'failed' || s === 'cancelled'), 5000);
    },
  });

  // startKey mode: the runId only exists after the initial fetch resolved it.
  const runId = directRunId ?? query.data?.runId ?? null;

  useEventSourceEvent(EVENT_TYPES.WORKFLOW.RUN_UPDATED, (payload) => {
    if (!runId || payload.runId !== runId) return;

    // At-least-once bridge → dedup replayed events.
    const eventId = payload.workflowEventId;
    if (typeof eventId === 'number') {
      if (seenEventIdsRef.current.has(eventId)) return;
      seenEventIdsRef.current.add(eventId);
    }

    const event = payload.event as string;
    if (event === 'workflow.completed' || event === 'workflow.failed') {
      // Terminal state → one authoritative refetch from the DB.
      void queryClient.invalidateQueries({ queryKey });
      return;
    }

    queryClient.setQueryData<WorkflowRunStatus | null>(queryKey, (prev) => {
      // Event raced ahead of the initial fetch — the fetch will include it.
      if (!prev) return prev;
      const stepId = typeof payload.stepId === 'string' ? payload.stepId : undefined;
      switch (event) {
        case 'step.started':
          return stepId ? { ...prev, currentStep: stepId } : prev;
        case 'step.progress': {
          if (!stepId) return prev;
          const patch: Partial<WorkflowRunStep> = {};
          if (typeof payload.current === 'number') patch.done = payload.current;
          if (typeof payload.total === 'number') patch.total = payload.total;
          return {
            ...prev,
            currentStep: stepId,
            steps: patchStep(prev.steps, stepId, patch),
          };
        }
        case 'step.completed': {
          if (!stepId) return prev;
          const step = prev.steps.find((s) => s.stepId === stepId);
          // Foreach step finished → done = total when known.
          const patch: Partial<WorkflowRunStep> =
            step?.total !== undefined ? { done: step.total } : {};
          return { ...prev, steps: patchStep(prev.steps, stepId, patch) };
        }
        default:
          return prev;
      }
    });
  });

  return {
    runId,
    status: query.data?.status ?? null,
    currentStep: query.data?.currentStep ?? null,
    steps: query.data?.steps ?? [],
    error: query.data?.error ?? null,
    isLoading: enabled && query.isLoading,
  };
}
